import React,{ forwardRef, useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import { Box } from '@mui/material';
import { ChartDataItem, ChartColorConfig } from '../types';
import {
    DEFAULT_CHART_MARGINS,
    PIE_LEGEND_CONFIG,
    PIE_LEGEND_BOTTOM_CONFIG,
    PIE_DEFS,
} from '../../../utils/chartConfig';

interface PieChartProps {
    data: ChartDataItem[];
    colors?: ChartColorConfig;
    innerRadius?: number;
    padAngle?: number;
    cornerRadius?: number;
    enableArcLinkLabels?: boolean;
    enableArcLabels?: boolean;
    onClick?: (node: { data: ChartDataItem }) => void;
    interactive?: boolean;
    height?: string | number;
    legendPosition?: 'top-left' | 'bottom';
}

export const PieChart = forwardRef<HTMLDivElement, PieChartProps>(
    (
        {
            data,
            colors = { scheme: 'nivo' },
            innerRadius = 0.5,
            padAngle = 0.7,
            cornerRadius = 3,
            enableArcLinkLabels = false,
            enableArcLabels = true,
            onClick,
            interactive = true,
            height = '100%',
            legendPosition = 'top-left',
        },
        ref
    ) => {
        const chartData = useMemo(() => {

            const aggregated = data.reduce<Record<string, ChartDataItem>>(
                (acc, item) => {
                    if (acc[item.id]) {
                        acc[item.id].value += item.value;
                    } else {
                        acc[item.id] = {
                            ...item,
                            color: item.color ?? undefined,
                        };
                    }
                    return acc;
                },
                {}
            );
            return Object.values(aggregated);
        }, [data]);

        const colorConfig = useMemo((): OrdinalColorScaleConfig => {
            if (colors.scheme) {
                return { scheme: colors.scheme } as OrdinalColorScaleConfig;
            }
            if (colors.datum) {
                return { datum: colors.datum } as OrdinalColorScaleConfig;
            }
            return { scheme: 'nivo' } as OrdinalColorScaleConfig;
        }, [colors]);

        const legendConfig = useMemo(() => {
            return legendPosition === 'bottom' ? PIE_LEGEND_BOTTOM_CONFIG : PIE_LEGEND_CONFIG;
        }, [legendPosition]);

        return (
            <Box ref={ref} sx={{ height, width: '100%' }}>
                <ResponsivePie
                    data={chartData}
                    margin={DEFAULT_CHART_MARGINS}
                    innerRadius={innerRadius}
                    padAngle={padAngle}
                    cornerRadius={cornerRadius}
                    activeOuterRadiusOffset={10}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.4]] }}
                    colors={colorConfig}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: 'color' }}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 1.8]] }}
                    enableArcLinkLabels={enableArcLinkLabels}
                    enableArcLabels={enableArcLabels}
                    isInteractive={interactive}
                    onClick={onClick as (node: unknown, event: unknown) => void}
                    defs={PIE_DEFS}
                    legends={[PIE_LEGEND_CONFIG]}
                    animate
                    motionConfig="gentle"
                />
            </Box>
        );
    }
);

PieChart.displayName = 'PieChart';
